import { useCallback } from 'react';
import { Trans } from 'react-i18next';
import { connect, type ConnectedProps } from 'react-redux';
import {
  InformationalScreen,
  LoadingModal,
  Text,
} from '@adc/parallax-component-library';
import { LocalDate } from '@js-joda/core';
import { type FormikHelpers, useFormik } from 'formik';
import * as z from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';

import { BirthdayInput, Footer } from '~/components/utility';
import { FormContainer } from '~/components/utility/FormContainer';
import { MainContainer } from '~/components/utility/MainContainer';
import { TopHeaderBackArrow } from '~/components/utility/TopHeaderBackArrow';
import { ApiStatus } from '~/constants';
import { type RootState } from '~/reducers';
import { verifyDateOfBirth } from '~/services/verify';
import { notifyApp } from '~/utilities/appCommunicator';
import { buildTestId } from '~/utilities/buildTestId';
import {
  formatCloudDateToFrontend,
  formatDateFromCloud,
} from '~/utilities/formatCloudDate';
import i18n from '~/utilities/i18n';
import mediator from '~/utilities/mediator';

const mapStateToProps = ({
  env: { deviceId, osType, osVersion, appVersion },
  auth: { include },
  nav: { query },
}: RootState) => ({
  deviceId,
  osType,
  osVersion,
  appVersion,
  patient: include?.patient,
  query,
});

const connector = connect(mapStateToProps);

type Props = ConnectedProps<typeof connector>;

type MyFormValues = {
  dateOfBirth: string | undefined;
};

const testID = 'verify-account';

const VerifyAccount = ({
  deviceId,
  osType,
  osVersion,
  appVersion,
  patient,
  query,
}: Props) => {
  const validationSchema = z.object({
    dateOfBirth: z.iso.date(
      i18n.t(
        'VerifyAccount.content.verifyAccountForm.formField.dateOfBirth.errors.required',
      ),
    ),
  });

  const onSubmit = useCallback(
    async (
      { dateOfBirth }: MyFormValues,
      { setFieldError }: FormikHelpers<MyFormValues>,
    ) => {
      const { data } = await verifyDateOfBirth({
        dateOfBirth,
        osType,
        osVersion,
        appVersion,
        deviceId,
      });

      if (data?.code === ApiStatus.DATE_OF_BIRTH_INVALID) {
        notifyApp('adc-webview:verify-account', { status: data.code, data });
        setFieldError(
          'dateOfBirth',
          i18n.t(
            'VerifyAccount.content.verifyAccountForm.formField.dateOfBirth.errors.invalid',
          ),
        );

        return;
      }

      notifyApp('adc-webview:verify-account', data);
      mediator.publish('router:redirect', {
        url: '/password-setup',
        query: { ...query },
      });
    },
    [appVersion, deviceId, osType, osVersion, query],
  );

  const {
    handleSubmit,
    setFieldTouched,
    setFieldValue,
    isValid,
    dirty,
    errors,
    touched,
    values,
    isSubmitting,
  } = useFormik<MyFormValues>({
    initialValues: {
      dateOfBirth: formatCloudDateToFrontend(patient?.dateOfBirth),
    },
    validationSchema: toFormikValidationSchema(validationSchema),
    onSubmit,
    validateOnBlur: true,
  });

  const handleDateChange = useCallback(
    (date: LocalDate, cancelled: boolean) => {
      void setFieldTouched('dateOfBirth');

      if (cancelled) {
        return;
      }

      const dateString = date.toString();

      void setFieldValue('dateOfBirth', dateString);
    },
    [setFieldTouched, setFieldValue],
  );

  return (
    <MainContainer testID={testID}>
      {isSubmitting && (
        <LoadingModal
          aria-label="Loading"
          testID={buildTestId({ testID, suffix: ['loading', 'modal'] })}
        />
      )}

      <InformationalScreen
        testID={buildTestId({ testID, suffix: 'screen' })}
        topNavigationProps={{
          animatedTitle: true,
          title: i18n.t('VerifyAccount.title'),
          buttonStart: {
            element: <TopHeaderBackArrow testID="nav-back" />,
            label: 'Go Back',
            onPress: () => {
              notifyApp('adc-webview:navigate-back-app');
            },
          },
          testID: 'nav-back',
        }}
      >
        <InformationalScreen.Page
          testID={buildTestId({ testID, suffix: 'page' })}
        >
          <InformationalScreen.Content>
            <Text marginTop={-8}>
              <Trans
                i18n={i18n}
                i18nKey="VerifyAccount.subtitle"
                values={{
                  email: patient?.email ?? 'unknown',
                }}
                components={{
                  Bold: <Text fontWeight="bold" />,
                }}
              />
            </Text>

            <FormContainer hasHiddenUserInput>
              <BirthdayInput
                error={Boolean(errors.dateOfBirth)}
                errorMessage={errors.dateOfBirth}
                isTouched={touched.dateOfBirth}
                initialDate={formatDateFromCloud(values.dateOfBirth)}
                onDateChange={handleDateChange}
                testID={buildTestId({ testID, suffix: ['dob', 'input'] })}
                endDate={LocalDate.now().minusYears(18)}
              />

              {errors.dateOfBirth ===
                i18n.t(
                  'VerifyAccount.content.verifyAccountForm.formField.dateOfBirth.errors.invalid',
                ) && (
                <Text marginTop="$4">
                  {i18n.t(
                    'VerifyAccount.content.verifyAccountForm.formField.dateOfBirth.errors.invalidHelpText',
                  )}
                </Text>
              )}
            </FormContainer>
          </InformationalScreen.Content>

          <InformationalScreen.Footer>
            <Footer
              buttonLabel={i18n.t(
                'VerifyAccount.content.verifyAccountForm.footerCreateAccount.submit.label',
              )}
              onButtonPress={handleSubmit}
              buttonTestID={buildTestId({
                testID,
                suffix: ['submit', 'button'],
              })}
              disabled={!isValid || !dirty || isSubmitting}
            />
          </InformationalScreen.Footer>
        </InformationalScreen.Page>
      </InformationalScreen>
    </MainContainer>
  );
};

export default connector(VerifyAccount);
